import { useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDiscoverSession } from "@/contexts/DiscoverSessionContext";
import { toast } from "sonner";
import { AnimatePresence } from "framer-motion";
import { Loader2, Star, Bookmark, Users, ChevronLeft, ChevronRight } from "lucide-react";
import SwipeCard from "@/components/SwipeCard";
import CountdownTimer from "@/components/CountdownTimer";
import type { CountdownTimerHandle } from "@/components/CountdownTimer";
import DiscoverSetup from "@/components/DiscoverSetup";
import type { DiscoverSettings } from "@/components/DiscoverSetup";
import ConfettiEffect from "@/components/ConfettiEffect";

import SynopsisModal from "@/components/SynopsisModal";
import TrailerModal from "@/components/TrailerModal";
import DiscoverResultModal from "@/components/DiscoverResultModal";
import SessionListPanel from "@/components/SessionListPanel";
import PickFlowModal from "@/components/PickFlowModal";
import GroupLobby from "@/components/GroupLobby";
import type { GroupSessionData } from "@/components/GroupLobby";
import GroupWaitingRoom from "@/components/GroupWaitingRoom";
import GroupResultsScreen from "@/components/GroupResultsScreen";
import GuestPostSession from "@/components/GuestPostSession";
import GoPremiumModal from "@/components/GoPremiumModal";
import { useAuth } from "@/contexts/AuthContext";
import { useUserData } from "@/hooks/useUserData";
import { fetchRecommendations, fetchTmdbDetails } from "@/lib/tmdb";
import { supabase } from "@/integrations/supabase/client";
import { submitGroupSwipes, markSwipingDone, calculateGroupResults, subscribeToSession, leaveGroupSession, endGroupSession, extendGroupQueue } from "@/lib/groupSession";
import type { MovieData } from "@/components/SwipeCard";
import TmdbAttribution from "@/components/TmdbAttribution";
// AdSense temporarily moved to content pages for approval — house ad shown here instead

const MOVIE_GENRE_MAP: Record<string, number> = {
  Action: 28, Adventure: 12, Animation: 16, Comedy: 35, Crime: 80,
  Documentary: 99, Drama: 18, Family: 10751, Fantasy: 14, History: 36,
  Horror: 27, Music: 10402, Mystery: 9648, Romance: 10749, "Sci-Fi": 878,
  Thriller: 53, War: 10752, Western: 37,
};

const TV_GENRE_MAP: Record<string, number> = {
  "Action & Adventure": 10759, Animation: 16, Comedy: 35, Crime: 80,
  Documentary: 99, Drama: 18, Family: 10751, History: 36, Horror: 27,
  Kids: 10762, Music: 10402, Mystery: 9648, Reality: 10764, Romance: 10749,
  "Sci-Fi & Fantasy": 10765, Soap: 10766, Talk: 10767, Thriller: 53,
  "War & Politics": 10768, Western: 37,
};

const Discover = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isPremium, user } = useAuth();
  const { addBookmark, removeBookmark, addToWatchHistory, getFavorites, addToReviewPending, getBookmarks, getWatchHistory, getCurrentlyWatching, addCurrentlyWatching, removeCurrentlyWatching, getHiddenTitles, addHiddenTitle, removeHiddenTitle } = useUserData();
  const { setSessionActive } = useDiscoverSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navState = location.state as { startGroup?: boolean; inviteFriendId?: string; inviteFriendUsername?: string; joinGroupCode?: string } | null;
  const joinCode = searchParams.get("join");
  const joinName = searchParams.get("name");
  const [settings, setSettings] = useState<DiscoverSettings | null>(null);
  const [movies, setMovies] = useState<MovieData[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<"left" | "right">("left");
  const [starred, setStarred] = useState<Set<number>>(new Set());
  const [bookmarked, setBookmarked] = useState<Set<number>>(new Set());
  const [currentlyWatchingSet, setCurrentlyWatchingSet] = useState<Set<number>>(new Set());
  const [hiddenSet, setHiddenSet] = useState<Set<number>>(new Set());
  const [seenRatings, setSeenRatings] = useState<Record<number, "loved" | "pretty_good" | "ok" | "not_great" | "hated">>({});
  const [forwardSwipes, setForwardSwipes] = useState(0);
  const [lastAdTime, setLastAdTime] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const discoverPageRef = useRef(1);
  const settingsRef = useRef<DiscoverSettings | null>(null);
  const moviesRef = useRef<MovieData[]>([]);
  const [adOverlayVisible, setAdOverlayVisible] = useState(false);
  const [adTimer, setAdTimer] = useState(10);
  const [adSkippable, setAdSkippable] = useState(false);
  const [exitAdVisible, setExitAdVisible] = useState(false);
  const [exitAdTimer, setExitAdTimer] = useState(10);
  const [exitAdSkippable, setExitAdSkippable] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [seenHistory, setSeenHistory] = useState<{ movie: MovieData; rating: string }[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [pickedMovie, setPickedMovie] = useState<MovieData | null>(null);
  
  const [synopsisMovie, setSynopsisMovie] = useState<MovieData | null>(null);
  const [trailerMovie, setTrailerMovie] = useState<MovieData | null>(null);
  const [showStarredPanel, setShowStarredPanel] = useState(false);
  const [showBookmarkedPanel, setShowBookmarkedPanel] = useState(false);
  const [showPickFlow, setShowPickFlow] = useState(false);
  const [showSettingsReminder, setShowSettingsReminder] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const timerRef = useRef<CountdownTimerHandle>(null);

  // Group mode state
  const [showGroupLobby, setShowGroupLobby] = useState(!!joinCode || !!navState?.startGroup || !!navState?.joinGroupCode);
  const [friendInviteId, setFriendInviteId] = useState<string | null>(navState?.inviteFriendId || null);
  const [friendInviteUsername, setFriendInviteUsername] = useState<string | null>(navState?.inviteFriendUsername || null);
  const [groupData, setGroupData] = useState<GroupSessionData | null>(null);
  const [groupPhase, setGroupPhase] = useState<"lobby" | "waiting" | "swiping" | "results" | "guest-left" | null>(null);
  const [groupResults, setGroupResults] = useState<{ winner: any; runners_up: any[]; random: boolean } | null>(null);
  const [groupSubmitted, setGroupSubmitted] = useState(false);
  const [groupWaitingForOthers, setGroupWaitingForOthers] = useState(false);
  const [groupTimerSeconds, setGroupTimerSeconds] = useState<number | null>(null);

  // Sync session active state for bottom nav visibility
  const isSessionActive = !!(settings || showGroupLobby || groupPhase);
  useEffect(() => {
    setSessionActive(isSessionActive);
    return () => setSessionActive(false);
  }, [isSessionActive, setSessionActive]);

  // Auto-join when coming from landing page with name pre-filled
  const autoJoinAttempted = useRef(false);
  useEffect(() => {
    if (joinCode && joinName && !autoJoinAttempted.current) {
      autoJoinAttempted.current = true;
      setSearchParams({}, { replace: true });
      // Directly join the session, skip lobby
      (async () => {
        try {
          const { joinGroupSession } = await import("@/lib/groupSession");
          const result = await joinGroupSession(joinCode, joinName, user?.id);
          handleGroupSessionJoined({ session: result.session, member: result.member, isHost: false, hostIsPremium: result.host_is_premium ?? false });
          setShowGroupLobby(false);
        } catch (e: any) {
          toast.error(e.message || t("common.error"));
          setShowGroupLobby(false);
        }
      })();
    } else if (joinCode) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  const starredMovies = movies.filter((m) => starred.has(m.id));
  const bookmarkedMovies = movies.filter((m) => bookmarked.has(m.id));

  const loadMovies = async (s: DiscoverSettings) => {
    setLoading(true);
    try {
      const favorites = await getFavorites();
      const favIds = favorites
        .filter((f: any) => f.media_type === s.mediaType)
        .map((f: any) => f.id);

      const userProviderIds = getUserProviderIds();
      const region = getUserRegion();

      // Map selected genre names to TMDB IDs using the correct map for media type
      const genreMap = s.mediaType === "movie" ? MOVIE_GENRE_MAP : TV_GENRE_MAP;
      const genreIds = s.genres.map((g) => genreMap[g]).filter(Boolean);

      // Build date range from selected decades for server-side filtering
      let dateRange: { gte?: string; lte?: string } | undefined;
      // Also build a set of valid years for client-side post-filtering (handles non-contiguous decades)
      let validYears: Set<number> | undefined;
      if (!s.decades.includes("any")) {
        const years: number[] = [];
        const now = new Date();
        for (const d of s.decades) {
          if (d === "Last 12 months") { years.push(now.getFullYear() - 1, now.getFullYear()); }
          else if (d === "2020s") { for (let y = 2020; y <= 2029; y++) years.push(y); }
          else if (d === "2010s") { for (let y = 2010; y <= 2019; y++) years.push(y); }
          else if (d === "2000s") { for (let y = 2000; y <= 2009; y++) years.push(y); }
          else if (d === "1990s") { for (let y = 1990; y <= 1999; y++) years.push(y); }
          else if (d === "1980s") { for (let y = 1980; y <= 1989; y++) years.push(y); }
          else if (d === "1970s") { for (let y = 1970; y <= 1979; y++) years.push(y); }
          else if (d === "1960s & earlier") { for (let y = 1900; y <= 1969; y++) years.push(y); }
        }
        if (years.length > 0) {
          validYears = new Set(years);
          const minYear = Math.min(...years);
          const maxYear = Math.max(...years);
          dateRange = { gte: `${minYear}-01-01`, lte: `${maxYear}-12-31` };
        }
      }

      // Read age flags from localStorage (set during onboarding / age gate)
      const is15Plus = localStorage.getItem("ayeris_is_15_plus") === "true";
      const is18Plus = localStorage.getItem("ayeris_is_18_plus") === "true";

      // Use the discover API with all filters baked in
      let data = await fetchRecommendations({
        favoriteIds: favIds,
        mediaType: s.mediaType,
        genreIds,
        providerIds: userProviderIds,
        maxRuntime: s.maxRuntime,
        region,
        dateRange,
        validYears: validYears ? Array.from(validYears) : [],
        is15Plus,
        is18Plus,
        userId: user?.id,
      });

      // Fallback: if no results, drop genres first (era takes precedence)
      if ((!data.results || data.results.length === 0) && genreIds.length > 0) {
        const activeFilters: string[] = [];
        if (dateRange) activeFilters.push(`from your selected era`);
        if (s.mediaType === 'movie' && s.maxRuntime < 180) activeFilters.push(`under ${s.maxRuntime + 10} minutes`);
        activeFilters.push(`on your streaming services`);
        const filterDesc = activeFilters.join(', ');
        toast.info(
          t("discover.genreFallback", { type: s.mediaType === 'movie' ? t("common.movies").toLowerCase() : t("common.tvShows").toLowerCase(), filters: filterDesc }),
          { duration: 7000 }
        );
        data = await fetchRecommendations({
          favoriteIds: favIds,
          mediaType: s.mediaType,
          genreIds: [],
          providerIds: userProviderIds,
          maxRuntime: s.maxRuntime,
          region,
          dateRange,
          validYears: validYears ? Array.from(validYears) : [],
          is15Plus,
          is18Plus,
          userId: user?.id,
        });
      }

      let items: MovieData[] = data.results.map((item: any) => ({
        id: item.id,
        title: item.title,
        synopsis: item.synopsis,
        poster: item.poster,
        year: item.year,
        runtime: item.runtime || 0,
        rating: item.rating,
        genres: item.genres || [],
        first_air_date: item.first_air_date || null,
        last_air_date: item.last_air_date || null,
        certification: item.certification || '',
        watch_link: item.watch_link || '',
        providers: (item.providers || []).map((p: any) => ({
          provider_id: p.provider_id,
          provider_name: p.provider_name,
          logo_path: p.logo_path || '',
        })),
      }));

      let enriched = items;

      // Filter out hidden titles and titles the user rated "hated" or "not_great"
      try {
        const [watchHistory, hiddenTitles] = await Promise.all([getWatchHistory(), getHiddenTitles()]);
        
        // Build hidden IDs set and persist for UI
        const hiddenIds = new Set(
          hiddenTitles
            .filter((h: any) => (h.media_type || "movie") === s.mediaType)
            .map((h: any) => h.id)
        );
        setHiddenSet(hiddenIds);
        if (hiddenIds.size > 0) {
          enriched = enriched.filter((m) => !hiddenIds.has(m.id));
        }

        const excludedIds = new Set(
          watchHistory
            .filter((h: any) => h.seenRating === "hated" || h.seenRating === "not_great")
            .map((h: any) => h.id)
        );
        if (excludedIds.size > 0) {
          enriched = enriched.filter((m) => !excludedIds.has(m.id));
        }
        // Pre-populate seenRatings so cards show existing ratings from watch history
        const preSeenRatings: Record<number, "loved" | "pretty_good" | "ok" | "not_great" | "hated"> = {};
        const validRatings = new Set(["loved", "pretty_good", "ok", "not_great", "hated"]);
        for (const h of watchHistory) {
          if (h.seenRating && validRatings.has(h.seenRating)) {
            preSeenRatings[h.id] = h.seenRating as any;
          }
        }
        setSeenRatings(preSeenRatings);
      } catch (e) {
        console.error("Error filtering watch history / hidden titles:", e);
      }

      // Post-filter by exact decade years (handles non-contiguous decade selections like 1970s + 1990s)
      if (validYears) {
        enriched = enriched.filter((m) => {
          if (s.mediaType === 'tv') {
            const firstYear = m.first_air_date ? new Date(m.first_air_date).getFullYear() : m.year;
            const lastYear = m.last_air_date ? new Date(m.last_air_date).getFullYear() : firstYear;
            return validYears!.has(firstYear) || validYears!.has(lastYear);
          }
          return validYears!.has(m.year);
        });
      }

      // Provider filtering is handled server-side — no client-side duplicate needed

      // No client-side shuffle — the server already handles ordering
      // (epsilon-greedy when no favorites, random shuffle otherwise)

      // Collect IDs that need TMDB enrichment (bookmarks + currently watching)
      const idsToEnrich: number[] = [];
      let bookmarkItems: MovieData[] = [];
      let cwItems: MovieData[] = [];
      const enrichRegion = getUserRegion();

      // Inject user bookmarks if the toggle is enabled
      if (s.includeBookmarks) {
        try {
          const userBookmarks = await getBookmarks();
          bookmarkItems = userBookmarks
            .filter((b: any) => (b.media_type || "movie") === s.mediaType && !enriched.some((m) => m.id === b.id))
            .map((b: MovieData) => ({ ...b, providers: b.providers || [] }));
          // Collect bookmark IDs that are missing synopsis
          for (const bm of bookmarkItems) {
            if (!bm.synopsis) idsToEnrich.push(bm.id);
          }
        } catch (e) {
          console.error("Error loading bookmarks for queue:", e);
        }
      }

      // Inject currently watching shows at the FRONT of the queue if toggled
      if (s.includeCurrentlyWatching && s.mediaType === "tv") {
        try {
          const cwShows = await getCurrentlyWatching();
          const cwIds = new Set<number>(cwShows.map((c: any) => c.id));
          setCurrentlyWatchingSet(cwIds);
          enriched = enriched.filter((m) => !cwIds.has(m.id));
          cwItems = cwShows.map((c: any) => ({
            id: c.id,
            title: c.title,
            synopsis: "",
            poster: c.poster || "",
            year: c.year || 0,
            runtime: 0,
            rating: 0,
            genres: [],
            providers: [],
          }));
          for (const cw of cwItems) idsToEnrich.push(cw.id);
        } catch (e) {
          console.error("Error loading currently watching for queue:", e);
        }
      } else {
        if (s.mediaType === "tv") {
          try {
            const cwShows = await getCurrentlyWatching();
            setCurrentlyWatchingSet(new Set<number>(cwShows.map((c: any) => c.id)));
          } catch {}
        }
      }

      // Enrich items missing metadata (synopsis, runtime, etc.) from TMDB
      if (idsToEnrich.length > 0) {
        try {
          const details = await fetchTmdbDetails(idsToEnrich, s.mediaType, enrichRegion);
          const applyDetails = (item: MovieData) => {
            const d = details[item.id];
            if (d) {
              item.synopsis = d.synopsis || item.synopsis;
              item.runtime = d.runtime || item.runtime;
              item.rating = d.rating || item.rating;
              item.genres = d.genres?.length ? d.genres : item.genres;
              item.certification = d.certification || item.certification;
              item.providers = d.providers?.length ? d.providers : item.providers;
              item.watch_link = d.watch_link || item.watch_link;
              item.year = d.year || item.year;
              item.poster = d.poster || item.poster;
              if (s.mediaType === "tv") {
                item.first_air_date = d.first_air_date || item.first_air_date;
                item.last_air_date = d.last_air_date || item.last_air_date;
              }
            }
            return item;
          };
          bookmarkItems = bookmarkItems.map(applyDetails);
          cwItems = cwItems.map(applyDetails);
        } catch (e) {
          console.error("Error enriching items with TMDB details:", e);
        }
      }

      // Build final queue order:
      // 1. Currently watching (TV only)
      // 2. Bookmarked titles
      // 3. Shuffled discovery results
      const front: MovieData[] = [];
      if (cwItems.length > 0) {
        front.push(...cwItems);
      }
      if (bookmarkItems.length > 0) {
        front.push(...bookmarkItems);
      }
      enriched = [...front, ...enriched];

      // Pre-populate bookmarked set so Save buttons appear highlighted for already-bookmarked titles
      try {
        const allUserBookmarks = await getBookmarks();
        const existingBookmarkIds = new Set(
          allUserBookmarks
            .filter((b: any) => (b.media_type || "movie") === s.mediaType)
            .map((b: any) => b.id)
        );
        setBookmarked(existingBookmarkIds);
      } catch (e) {
        console.error("Error pre-populating bookmarked set:", e);
      }

      moviesRef.current = enriched;
      setMovies(enriched);
    } catch (e) {
      console.error("Error loading recommendations:", e);
    } finally {
      setLoading(false);
    }
  };

  const getUserProviderIds = (): number[] => {
    // Read from localStorage cache (populated by usePreferences on app init)
    try {
      const raw = localStorage.getItem("ayeris_providers");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };

  const getUserRegion = (): string => {
    return localStorage.getItem("ayeris_region") || "GB";
  };

  const handleCurrentlyWatching = () => {
    const movie = deck[currentIndex];
    if (movie && settings?.mediaType === "tv") {
      setCurrentlyWatchingSet((prev) => {
        const next = new Set(prev);
        if (next.has(movie.id)) {
          next.delete(movie.id);
          removeCurrentlyWatching(movie.id);
          toast.info(t("discover.removedFromCurrentlyWatching"));
        } else {
          next.add(movie.id);
          addCurrentlyWatching({ id: movie.id, title: movie.title, poster: movie.poster, year: movie.year, media_type: "tv" });
          toast.success(t("discover.addedToCurrentlyWatching"));
        }
        return next;
      });
    }
  };

  const handleHide = () => {
    const movie = deck[currentIndex];
    if (movie) {
      setHiddenSet((prev) => {
        const next = new Set(prev);
        if (next.has(movie.id)) {
          next.delete(movie.id);
          removeHiddenTitle(movie.id);
          toast.info(t("discover.unhidden"));
        } else {
          next.add(movie.id);
          addHiddenTitle(movie, settings?.mediaType);
          toast.success(t("discover.hidden"));
        }
        return next;
      });
    }
  };


  const handleSetupComplete = (s: DiscoverSettings) => {
    setSettings(s);
    settingsRef.current = s;
    discoverPageRef.current = 1;
    setForwardSwipes(0);
    setAdOverlayVisible(false);
    loadMovies(s);
  };

  // Fetch more recommendations when running low
  const fetchMoreMovies = useCallback(async () => {
    const s = settingsRef.current;
    if (!s || isFetchingMoreRef.current) return;

    // Group session: only the host triggers extend_queue on the server
    if (groupPhase === 'swiping' && groupData) {
      const isHost = groupData.member.id === groupData.session.host_member_id;
      if (!isHost) return;

      isFetchingMoreRef.current = true;
      setIsFetchingMore(true);
      try {
        const nextPage = discoverPageRef.current + 5;
        discoverPageRef.current = nextPage;
        const userProviderIds = getUserProviderIds();
        const region = getUserRegion();

        const result = await extendGroupQueue(
          groupData.session.id,
          nextPage,
          userProviderIds,
          region
        );

        if (result.new_items && result.new_items.length > 0) {
          const newItems: MovieData[] = result.new_items.map((item: any) => ({
            id: item.id,
            title: item.title,
            synopsis: item.synopsis || '',
            poster: item.poster || '',
            year: item.year || 0,
            runtime: item.runtime || 0,
            rating: item.rating || 0,
            genres: item.genres || [],
            first_air_date: item.first_air_date || null,
            last_air_date: item.last_air_date || null,
            certification: item.certification || '',
            watch_link: item.watch_link || '',
            providers: (item.providers || []).map((p: any) => ({
              provider_id: p.provider_id,
              provider_name: p.provider_name,
              logo_path: p.logo_path || '',
            })),
          }));
          moviesRef.current = [...moviesRef.current, ...newItems];
          setMovies(moviesRef.current);
        }
      } catch (e) {
        console.error("Error extending group queue:", e);
      } finally {
        isFetchingMoreRef.current = false;
        setIsFetchingMore(false);
      }
      return;
    }

    // Solo session
    if (groupPhase) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const nextPage = discoverPageRef.current + 5;
      discoverPageRef.current = nextPage;

      const favorites = await getFavorites();
      const favIds = favorites.filter((f: any) => f.media_type === s.mediaType).map((f: any) => f.id);
      const userProviderIds = getUserProviderIds();
      const region = getUserRegion();
      const genreMap = s.mediaType === "movie" ? MOVIE_GENRE_MAP : TV_GENRE_MAP;
      const genreIds = s.genres.map((g) => genreMap[g]).filter(Boolean);

      let dateRange: { gte?: string; lte?: string } | undefined;
      let validYears: Set<number> | undefined;
      if (!s.decades.includes("any")) {
        const years: number[] = [];
        const now = new Date();
        for (const d of s.decades) {
          if (d === "Last 12 months") { years.push(now.getFullYear() - 1, now.getFullYear()); }
          else if (d === "2020s") { for (let y = 2020; y <= 2029; y++) years.push(y); }
          else if (d === "2010s") { for (let y = 2010; y <= 2019; y++) years.push(y); }
          else if (d === "2000s") { for (let y = 2000; y <= 2009; y++) years.push(y); }
          else if (d === "1990s") { for (let y = 1990; y <= 1999; y++) years.push(y); }
          else if (d === "1980s") { for (let y = 1980; y <= 1989; y++) years.push(y); }
          else if (d === "1970s") { for (let y = 1970; y <= 1979; y++) years.push(y); }
          else if (d === "1960s & earlier") { for (let y = 1900; y <= 1969; y++) years.push(y); }
        }
        if (years.length > 0) {
          validYears = new Set(years);
          const minYear = Math.min(...years);
          const maxYear = Math.max(...years);
          dateRange = { gte: `${minYear}-01-01`, lte: `${maxYear}-12-31` };
        }
      }

      const is15Plus = localStorage.getItem("ayeris_is_15_plus") === "true";
      const is18Plus = localStorage.getItem("ayeris_is_18_plus") === "true";

      // Collect all IDs already in the queue to exclude
      const existingIds = moviesRef.current.map((m) => m.id);

      const data = await fetchRecommendations({
        favoriteIds: favIds,
        mediaType: s.mediaType,
        genreIds,
        providerIds: userProviderIds,
        maxRuntime: s.maxRuntime,
        region,
        dateRange,
        validYears: validYears ? Array.from(validYears) : [],
        is15Plus,
        is18Plus,
        excludeIds: existingIds,
        discoverPageStart: nextPage,
        userId: user?.id,
      });

      if (data.results && data.results.length > 0) {
        let newItems: MovieData[] = data.results.map((item: any) => ({
          id: item.id,
          title: item.title,
          synopsis: item.synopsis,
          poster: item.poster,
          year: item.year,
          runtime: item.runtime || 0,
          rating: item.rating,
          genres: item.genres || [],
          first_air_date: item.first_air_date || null,
          last_air_date: item.last_air_date || null,
          certification: item.certification || '',
          watch_link: item.watch_link || '',
          providers: (item.providers || []).map((p: any) => ({
            provider_id: p.provider_id,
            provider_name: p.provider_name,
            logo_path: p.logo_path || '',
          })),
        }));

        // Filter out hidden titles
        if (hiddenSet.size > 0) {
          newItems = newItems.filter((m) => !hiddenSet.has(m.id));
        }

        // Filter by provider
        if (userProviderIds.length > 0) {
          newItems = newItems.filter((m) =>
            m.providers && m.providers.some((p) => userProviderIds.includes(p.provider_id))
          );
        }

        // Filter by exact years
        if (validYears) {
          newItems = newItems.filter((m) => {
            if (s.mediaType === 'tv') {
              const firstYear = m.first_air_date ? new Date(m.first_air_date).getFullYear() : m.year;
              const lastYear = m.last_air_date ? new Date(m.last_air_date).getFullYear() : firstYear;
              return validYears!.has(firstYear) || validYears!.has(lastYear);
            }
            return validYears!.has(m.year);
          });
        }

        if (newItems.length > 0) {
          // Shuffle new batch to avoid genre/era clustering
          for (let i = newItems.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newItems[i], newItems[j]] = [newItems[j], newItems[i]];
          }
          moviesRef.current = [...moviesRef.current, ...newItems];
          setMovies(moviesRef.current);
        }
      }
    } catch (e) {
      console.error("Error fetching more recommendations:", e);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  }, [groupPhase, groupData, hiddenSet]);

  // Auto-fetch more when within 10 cards of the end (works for both solo and group)
  useEffect(() => {
    if (!settings) return;
    const remaining = movies.length - currentIndex;
    if (remaining <= 10 && remaining > 0 && !isFetchingMoreRef.current) {
      fetchMoreMovies();
    }
  }, [currentIndex, movies.length, settings, fetchMoreMovies]);

  // Deck is just movies — no ad cards
  const deck = movies;

  // Show ad overlay when forward swipes hit 10 AND at least 90s since last ad
  const triggerAd = () => {
    setAdOverlayVisible(true);
    setAdTimer(10);
    setAdSkippable(false);
    setLastAdTime(Date.now());
    const interval = setInterval(() => {
      setAdTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setAdSkippable(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const dismissAd = () => {
    setAdOverlayVisible(false);
    setForwardSwipes(0);
  };

  const exitAdCallbackRef = useRef<() => void>(() => navigate("/"));

  const triggerExitAd = (afterDismiss?: () => void) => {
    exitAdCallbackRef.current = afterDismiss || (() => navigate("/"));
    if (shouldSuppressAds) {
      exitAdCallbackRef.current();
      return;
    }
    setExitAdVisible(true);
    setExitAdTimer(10);
    setExitAdSkippable(false);
    const interval = setInterval(() => {
      setExitAdTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setExitAdSkippable(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const dismissExitAd = () => {
    setExitAdVisible(false);
    exitAdCallbackRef.current();
  };

  // Determine if ads should be suppressed for this user
  const shouldSuppressAds = isPremium || (!!groupData && groupData.hostIsPremium);

  // Navigate forward/back — only forward swipes count toward ad trigger

  const goNext = () => {
    if (adOverlayVisible) return;
    if (!shouldSuppressAds) {
      const nextSwipes = forwardSwipes + 1;
      const timeSinceLastAd = Date.now() - lastAdTime;
      if (nextSwipes >= 10 && timeSinceLastAd >= 90000) {
        triggerAd();
        setForwardSwipes(0);
      } else if (nextSwipes >= 10 && timeSinceLastAd < 90000) {
        // Hit 10 swipes but cooldown not met — keep counting, trigger when cooldown passes
        setForwardSwipes(nextSwipes);
      } else {
        setForwardSwipes(nextSwipes);
      }
    }
    setSwipeDirection("left");
    setCurrentIndex((prev) => Math.min(prev + 1, deck.length - 1));
  };
  const goPrev = () => {
    if (adOverlayVisible) return;
    setSwipeDirection("right");
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSeen = (rating: "loved" | "pretty_good" | "ok" | "not_great" | "hated") => {
    const movie = deck[currentIndex];
    if (movie) {
      // Toggle: if same rating clicked again, unset it
      setSeenRatings((prev) => {
        if (prev[movie.id] === rating) {
          const next = { ...prev };
          delete next[movie.id];
          return next;
        }
        return { ...prev, [movie.id]: rating };
      });
      // Save to watch history
      addToWatchHistory(movie, rating, false, settings?.mediaType);
    }
  };

  const handleStar = () => {
    const movie = deck[currentIndex];
    if (movie) {
      setStarred((prev) => {
        const next = new Set(prev);
        if (next.has(movie.id)) next.delete(movie.id);
        else next.add(movie.id);
        return next;
      });
    }
  };

  const handleBookmark = () => {
    const movie = deck[currentIndex];
    if (movie) {
      setBookmarked((prev) => {
        const next = new Set(prev);
        const isRemoving = next.has(movie.id);
        if (isRemoving) {
          next.delete(movie.id);
          removeBookmark(movie.id);
        } else {
          next.add(movie.id);
          addBookmark(movie, settings?.mediaType);
        }
        return next;
      });
    }
  };

  const handleRemoveStarred = (id: number) => {
    setStarred((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleRemoveBookmarked = (id: number) => {
    setBookmarked((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    removeBookmark(id);
  };

  const handleGroupSubmitPicks = useCallback(async () => {
    if (!groupData || groupPhase !== 'swiping') return;
    setGroupSubmitted(true);
    setGroupWaitingForOthers(true);

    const swipes: { tmdb_id: number; action: string }[] = [
      ...[...starred].map(id => ({ tmdb_id: id, action: 'star' })),
      ...[...bookmarked].map(id => ({ tmdb_id: id, action: 'bookmark' })),
    ];

    try {
      await submitGroupSwipes(groupData.member.id, groupData.session.id, swipes);
      const markResult = await markSwipingDone(groupData.member.id);
      if (markResult.all_done && markResult.winner !== undefined) {
        setGroupResults({ winner: markResult.winner, runners_up: markResult.runners_up || [], random: markResult.random || false });
        setGroupPhase('results');
      }
    } catch (e: any) {
      toast.error(t("common.error"));
      setGroupSubmitted(false);
      setGroupWaitingForOthers(false);
    }
  }, [groupData, groupPhase, starred, bookmarked]);

  // Watch for session status changes during swiping
  useEffect(() => {
    if (!groupData || groupPhase !== 'swiping') return;

    const guestId = groupData.member.guest_id || undefined;
    const channel = subscribeToSession(groupData.session.id, async (session) => {
      if (session.status === 'results' && !groupResults && groupSubmitted) {
        try {
          const results = await calculateGroupResults(groupData.session.id);
          setGroupResults(results);
          setGroupPhase('results');
        } catch (e: any) {
          toast.error(t("common.error"));
        }
      }
      // Host ended the session — kick everyone out
      if (session.status === 'closed') {
        if (!user) {
          setGroupPhase('guest-left');
        } else {
          handleGroupClosed();
          toast.info(t("discoverSession.hostEnded"));
        }
      }
      // Non-host members: detect queue extension and append new items
      const isHost = groupData.member.id === groupData.session.host_member_id;
      if (!isHost && session.queue && Array.isArray(session.queue)) {
        setMovies((prev) => {
          if (session.queue.length > prev.length) {
            const newItems = session.queue.slice(prev.length).map((item: any) => ({
              id: item.id,
              title: item.title,
              synopsis: item.synopsis || '',
              poster: item.poster || '',
              year: item.year || 0,
              runtime: item.runtime || 0,
              rating: item.rating || 0,
              genres: item.genres || [],
              first_air_date: item.first_air_date || null,
              last_air_date: item.last_air_date || null,
              certification: item.certification || '',
              watch_link: item.watch_link || '',
              providers: (item.providers || []).map((p: any) => ({
                provider_id: p.provider_id,
                provider_name: p.provider_name,
                logo_path: p.logo_path || '',
              })),
            }));
            return [...prev, ...newItems];
          }
          return prev;
        });
      }
    }, guestId);
    return () => { channel.unsubscribe(); };
  }, [groupData, groupPhase, groupSubmitted, groupResults, user]);

  const handleTimerComplete = useCallback(async () => {
    if (groupData && groupPhase === 'swiping') {
      // Timer expired — submit this user's picks, then immediately calculate results for everyone
      setGroupSubmitted(true);
      setGroupWaitingForOthers(true);

      const swipes: { tmdb_id: number; action: string }[] = [
        ...[...starred].map(id => ({ tmdb_id: id, action: 'star' })),
        ...[...bookmarked].map(id => ({ tmdb_id: id, action: 'bookmark' })),
      ];

      try {
        await submitGroupSwipes(groupData.member.id, groupData.session.id, swipes);
        const markResult = await markSwipingDone(groupData.member.id);
        // If server calculated results (all members done), use them directly
        if (markResult.all_done && markResult.winner !== undefined) {
          setGroupResults({ winner: markResult.winner, runners_up: markResult.runners_up || [], random: markResult.random || false });
          setGroupPhase('results');
        }
        // Otherwise, realtime subscription will catch when all members are done
      } catch (e: any) {
        toast.error(t("common.error"));
      }
      return;
    }
    const pool = starredMovies.length > 0 ? starredMovies : bookmarkedMovies.length > 0 ? bookmarkedMovies : movies;
    if (pool.length > 0) {
      const picked = pool[Math.floor(Math.random() * pool.length)];
      setPickedMovie(picked);
      addToReviewPending(picked, settings?.mediaType);
    }
    setShowResult(true);
  }, [starredMovies, bookmarkedMovies, groupData, groupPhase, starred, bookmarked]);

  const handlePicked = (movie: MovieData) => {
    addToReviewPending(movie, settings?.mediaType);
  };

  // Group session handlers
  const handleGroupSessionJoined = async (data: GroupSessionData) => {
    setGroupData(data);
    setGroupPhase('waiting');
    
    // If we came from friends page with an invite, send the group invite
    if (friendInviteId && data.isHost && data.session?.id) {
      try {
        await (supabase.from("group_invites") as any).insert({
          session_id: data.session.id,
          from_user_id: user?.id,
          to_user_id: friendInviteId,
          status: "pending",
        });
        toast.success(t("friends.inviteSent", { name: friendInviteUsername || "friend" }));
      } catch {}
      setFriendInviteId(null);
      setFriendInviteUsername(null);
    }
  };

  const handleGroupQueueReady = async (queue: MovieData[], session: any) => {
    setMovies(queue);

    // Fetch actual member preferences to display in settings drawer
    let groupGenres: string[] = [];
    let groupDecades: string[] = ["any"];
    try {
      const { data: members } = await supabase
        .from('group_members')
        .select('genres, decades')
        .eq('session_id', session.id);
      if (members) {
        const genreSet = new Set<string>();
        const decadeSet = new Set<string>();
        let anyGenre = false;
        let anyDecade = false;
        for (const m of members) {
          const g = m.genres || [];
          const d = m.decades || [];
          if (g.length === 0 || g.includes('any')) anyGenre = true;
          else g.forEach((x: string) => genreSet.add(x));
          if (d.length === 0 || d.includes('any')) anyDecade = true;
          else d.forEach((x: string) => decadeSet.add(x));
        }
        groupGenres = anyGenre ? [] : Array.from(genreSet);
        groupDecades = anyDecade ? ["any"] : Array.from(decadeSet);
      }
    } catch {}

    // Calculate remaining timer from server timestamp
    const totalTimerSecs = session.decision_timer * 60;
    if (session.swiping_started_at) {
      const startedAt = new Date(session.swiping_started_at).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - startedAt) / 1000);
      const remaining = Math.max(totalTimerSecs - elapsed, 1);
      setGroupTimerSeconds(remaining);
    } else {
      setGroupTimerSeconds(totalTimerSecs);
    }

    setSettings({
      mediaType: session.media_type,
      maxRuntime: session.max_runtime,
      decisionTimer: session.decision_timer,
      genres: groupGenres,
      decades: groupDecades,
      
      includeBookmarks: false,
      includeCurrentlyWatching: false,
    });
    setGroupPhase('swiping');
  };

  const handleGroupClosed = () => {
    setGroupData(null);
    setGroupPhase(null);
    setGroupResults(null);
    setGroupSubmitted(false);
    setGroupWaitingForOthers(false);
    setSettings(null);
    setMovies([]);
    setCurrentIndex(0);
    setStarred(new Set());
    setBookmarked(new Set());
    toast.success(t("discoverSession.sessionComplete"));
  };

  // Group flow screens
  if (showGroupLobby && !groupData) {
    return <GroupLobby onSessionJoined={handleGroupSessionJoined} onBack={() => setShowGroupLobby(false)} initialCode={joinCode || navState?.joinGroupCode || undefined} />;
  }

  if (groupPhase === 'waiting' && groupData) {
    return <GroupWaitingRoom groupData={groupData} onQueueReady={handleGroupQueueReady} onLeave={() => {
      setGroupData(null);
      setGroupPhase(null);
      setShowGroupLobby(false);
      if (!user) {
        // Guest — go back to landing
        window.location.href = '/landing';
      } else {
        toast.info(t("discoverSession.leftGroup"));
      }
    }} />;
  }

  if (groupPhase === 'results' && groupData && groupResults) {
    return (
      <GroupResultsScreen
        sessionId={groupData.session.id}
        memberId={groupData.member.id}
        winner={groupResults.winner}
        runnersUp={groupResults.runners_up}
        isRandom={groupResults.random}
        isGuest={!user}
        guestDisplayName={groupData.member.display_name}
        guestId={groupData.member.guest_id || undefined}
        onClosed={handleGroupClosed}
        onDone={() => triggerExitAd(handleGroupClosed)}
      />
    );
  }

  // Guest left or was kicked — show CTA or go home
  if ((groupPhase === 'guest-left' || (groupPhase === 'results' && !groupResults)) && groupData) {
    if (!user) {
      return <GuestPostSession suggestedUsername={groupData.member.display_name} />;
    }
    handleGroupClosed();
    return null;
  }

  if (!settings) return <DiscoverSetup onStart={handleSetupComplete} onGroupMode={() => setShowGroupLobby(true)} />;

  if (loading) {
    return (
      <div className="h-[100dvh] bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">{t("discoverSession.findingPicks")}</p>
        </div>
      </div>
    );
  }

  const currentMovie = deck[currentIndex];
  const mediaType = settings.mediaType;

  return (
    <div className="h-[100dvh] bg-background flex flex-col overflow-hidden">
      {/* Settings reminder drawer */}
      {showSettingsReminder && (
        <div className="mx-4 mt-2 mb-1 p-3 rounded-xl bg-secondary/80 backdrop-blur border border-border animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
              {groupPhase ? t("discoverSession.groupSessionLabel") : t("discover.settingsReminder")}
            </span>
            <button onClick={() => setShowSettingsReminder(false)} className="text-xs text-muted-foreground">✕</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-medium">
              {settings.mediaType === "movie" ? `🎬 ${t("common.movies")}` : `📺 ${t("common.tvShows")}`}
            </span>
            {settings.mediaType === "movie" && (
              <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-medium">
                ⏱ {settings.maxRuntime >= 180 ? "3h+" : `${Math.floor(settings.maxRuntime / 60)}h${settings.maxRuntime % 60 > 0 ? ` ${settings.maxRuntime % 60}m` : ""}`}
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-medium">
              ⏳ {t("discover.timerLabel", { min: settings.decisionTimer })}
            </span>
            {settings.genres.map((g) => (
              <span key={g} className="px-2 py-0.5 rounded-full bg-accent/30 text-accent-foreground text-[10px] font-medium">{g}</span>
            ))}
            {settings.genres.length === 0 && (
              <span className="text-[10px] text-muted-foreground">{t("discoverSession.allGenres")}</span>
            )}
            <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-medium">
              🗓 {settings.decades.includes("any") ? t("discoverSession.anyEra") : settings.decades.join(", ")}
            </span>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-2 pb-1 shrink-0">
        <CountdownTimer
          ref={timerRef}
          totalSeconds={groupData && groupTimerSeconds !== null ? groupTimerSeconds : settings.decisionTimer * 60}
          onComplete={handleTimerComplete}
          startPaused={false}
        />
        <div className="flex items-center gap-2">
          {/* Settings reminder toggle */}
          <button
            onClick={() => setShowSettingsReminder((p) => !p)}
            className="px-2 py-1.5 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
            title="Session settings"
          >
            <span className="text-xs">⚙️</span>
          </button>
          {/* Starred count - clickable (toggle) */}
          <button
            onClick={() => { setShowBookmarkedPanel(false); setShowStarredPanel((p) => !p); }}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-star/10 hover:bg-star/20 transition-colors"
          >
            <Star className="w-4 h-4 text-star fill-star" />
            <span className="text-xs font-medium text-star">{starred.size}</span>
          </button>
          {/* Bookmarked count - clickable (toggle), hidden for group guests */}
          {!(groupPhase && !user) && (
            <button
              onClick={() => { setShowStarredPanel(false); setShowBookmarkedPanel((p) => !p); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-bookmark/10 hover:bg-bookmark/20 transition-colors"
            >
              <Bookmark className="w-4 h-4 text-bookmark fill-bookmark" />
              <span className="text-xs font-medium text-bookmark">{bookmarked.size}</span>
            </button>
          )}
          <button
            onClick={() => setShowEndConfirm((p) => !p)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-destructive/10 hover:bg-destructive/20 text-destructive text-xs font-medium ml-1 transition-colors"
          >
            ✕ {t("discoverSession.end")}
          </button>
        </div>
      </div>

      {/* End session confirmation */}
      {showEndConfirm && (
        <div className="mx-4 mb-1 p-3 rounded-xl bg-destructive/10 border border-destructive/30 animate-in fade-in slide-in-from-top-2 duration-200">
          <p className="text-xs text-foreground mb-2">
            {groupPhase && groupData?.isHost
              ? t("discoverSession.endForEveryone")
              : groupPhase && !user
              ? t("discoverSession.leaveGroup")
              : t("discoverSession.endSessionDesc")}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                setShowEndConfirm(false);
                if (groupPhase && groupData) {
                  if (groupData.isHost) {
                    // Host ends the session for everyone
                    try {
                      await endGroupSession(groupData.session.id);
                      handleGroupClosed();
                    } catch (e: any) {
                      toast.error(e.message || t("common.error"));
                    }
                  } else {
                    // Guest leaves gracefully
                    try {
                      await leaveGroupSession(groupData.member.id, groupData.session.id);
                      if (!user) {
                        // Show guest CTA
                        setGroupPhase('results');
                        setGroupResults(null); // Signal to show CTA directly
                      } else {
                        handleGroupClosed();
                      }
                    } catch (e: any) {
                      toast.error(e.message || t("common.error"));
                    }
                  }
                } else {
                  setSettings(null); setMovies([]); setCurrentIndex(0); setStarred(new Set()); setBookmarked(new Set());
                }
              }}
              className="px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-xs font-medium active:scale-95 transition-transform"
            >
              {groupPhase && groupData?.isHost ? t("discoverSession.yesEndEveryone") : groupPhase ? t("discoverSession.yesLeave") : t("discover.yesEnd")}
            </button>
            <button
              onClick={() => setShowEndConfirm(false)}
              className="px-3 py-1.5 rounded-lg bg-secondary text-secondary-foreground text-xs font-medium active:scale-95 transition-transform"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}


      {/* Card stack */}
      <div className="flex-1 relative min-h-0 overflow-hidden" onClick={() => { setShowStarredPanel(false); setShowBookmarkedPanel(false); }}>
        {/* Navigation arrows */}
        {!adOverlayVisible && currentIndex > 0 && (
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-secondary/80 backdrop-blur flex items-center justify-center active:scale-90 transition-transform"
          >
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
        )}
        {!adOverlayVisible && currentIndex < deck.length - 1 && (
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-secondary/80 backdrop-blur flex items-center justify-center active:scale-90 transition-transform"
          >
            <ChevronRight className="w-5 h-5 text-foreground" />
          </button>
        )}
        {/* Subtle loading indicator when fetching more in background */}
        {isFetchingMore && currentIndex < deck.length && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/90 backdrop-blur-sm">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">{t("discover.loadingMore", "Loading more titles...")}</span>
          </div>
        )}

        <AnimatePresence mode="popLayout" custom={swipeDirection}>
          {deck.slice(currentIndex, currentIndex + 2).map((movie, i) => (
            <SwipeCard
              key={`${movie.id}-${currentIndex + i}`}
              movie={movie}
              isTop={i === 0}
              direction={swipeDirection}
              onSwipeLeft={goPrev}
              onSwipeRight={goNext}
              onSeen={handleSeen}
              seenRating={seenRatings[movie.id] || null}
              onStar={handleStar}
              onBookmark={handleBookmark}
              onReadMore={() => setSynopsisMovie(movie)}
              isStarred={starred.has(movie.id)}
              isBookmarked={bookmarked.has(movie.id)}
              hideSeen={!!groupPhase && !user}
              hideBookmark={!!groupPhase && !user}
              mediaType={settings?.mediaType}
              onCurrentlyWatching={settings?.mediaType === "tv" ? handleCurrentlyWatching : undefined}
              isCurrentlyWatching={currentlyWatchingSet.has(movie.id)}
              onHide={!(groupPhase && !user) ? handleHide : undefined}
              isHidden={hiddenSet.has(movie.id)}
            />
          ))}
        </AnimatePresence>

        {/* Ad overlay */}
        {adOverlayVisible && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="w-full max-w-sm mx-4 rounded-2xl bg-gradient-card border border-border overflow-hidden shadow-card">
              <div className="aspect-[2/3] flex flex-col items-center justify-center bg-secondary relative">
                <div className="w-full px-4 flex-1 flex items-center justify-center flex-col gap-4">
                  <span className="text-4xl">🍿</span>
                  <p className="text-sm font-display font-semibold text-foreground text-center">Ayeris Premium</p>
                  <p className="text-xs text-muted-foreground text-center max-w-[200px]">{t("goPremium.removeAds")}</p>
                </div>
                <div className="absolute bottom-4 right-4 z-30 flex flex-col items-end gap-2">
                  {adSkippable ? (
                    <button
                      onClick={dismissAd}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold active:scale-95 transition-transform"
                    >
                      {t("discover.adSkip")} →
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-muted-foreground text-sm font-medium">
                      <div className="relative w-5 h-5">
                        <svg className="w-full h-full -rotate-90" viewBox="0 0 20 20">
                          <circle cx="10" cy="10" r="8" fill="none" stroke="hsl(var(--border))" strokeWidth="2" />
                          <circle
                            cx="10" cy="10" r="8" fill="none"
                            stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round"
                            strokeDasharray={50.3}
                            strokeDashoffset={50.3 * (1 - adTimer / 10)}
                            style={{ transition: 'stroke-dashoffset 1s linear' }}
                          />
                        </svg>
                      </div>
                      <span className="tabular-nums">{adTimer}s</span>
                    </div>
                  )}
                  <button
                    onClick={() => setShowPremiumModal(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-accent-foreground text-xs font-semibold active:scale-95 transition-transform"
                  >
                    ✨ {t("goPremium.removeAds")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentIndex >= deck.length && !showResult && (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center">
            {isFetchingMore ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{t("discover.loadingMore", "Loading more titles...")}</p>
              </div>
            ) : (
              <div>
                <span className="text-5xl mb-4 block">🍿</span>
                <h3 className="font-display text-xl font-bold text-foreground mb-2">{t("discoverSession.thatsAWrap")}</h3>
                <p className="text-sm text-muted-foreground">{t("discoverSession.goneThrough")}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* TMDB attribution */}
      <TmdbAttribution className="py-1 opacity-60" />

      {/* Bottom action bar */}
      <div className="px-4 pb-2 pt-1 flex items-center gap-2 shrink-0 safe-area-bottom">
        {groupPhase === 'swiping' ? (
          <button
            onClick={handleGroupSubmitPicks}
            disabled={groupSubmitted}
            className="flex-1 py-3 rounded-full bg-gradient-iris text-primary-foreground font-semibold text-sm shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {groupWaitingForOthers ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("discoverSession.waitingForOthers")}
              </>
            ) : (
              <>
                <Users className="w-4 h-4" />
                {t("discoverSession.submitPicks")} {starred.size > 0 && `(${starred.size})`}
              </>
            )}
          </button>
        ) : (
      starred.size >= 1 && (
            <button
              onClick={() => setShowPickFlow(true)}
              className="flex-1 py-3 rounded-full bg-gradient-iris text-primary-foreground font-semibold text-sm shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2"
            >
              🎬 {t("discoverSession.submitPicks")} ({starred.size})
            </button>
          )
        )}
      </div>


      {/* Synopsis modal */}
      <SynopsisModal
        movie={synopsisMovie}
        open={!!synopsisMovie}
        onClose={() => setSynopsisMovie(null)}
        onWatchTrailer={() => {
          const m = synopsisMovie;
          setSynopsisMovie(null);
          if (m) setTrailerMovie(m);
        }}
      />

      {/* Trailer modal */}
      <TrailerModal
        movieId={trailerMovie?.id || 0}
        mediaType={mediaType}
        title={trailerMovie?.title || ""}
        open={!!trailerMovie}
        onClose={() => setTrailerMovie(null)}
      />

      {/* Session list panels */}
      <SessionListPanel
        open={showStarredPanel}
        onClose={() => setShowStarredPanel(false)}
        title={t("discoverSession.starredTonight")}
        icon="star"
        items={starredMovies}
        onRemove={handleRemoveStarred}
      />
      <SessionListPanel
        open={showBookmarkedPanel}
        onClose={() => setShowBookmarkedPanel(false)}
        title={t("discover.bookmarkedList")}
        icon="bookmark"
        items={bookmarkedMovies}
        onRemove={handleRemoveBookmarked}
      />

      {/* Pick flow */}
      <AnimatePresence>
        {showPickFlow && (
          <PickFlowModal
            open={showPickFlow}
            starredItems={starredMovies}
            onClose={() => setShowPickFlow(false)}
            onPicked={handlePicked}
            onRemoveStar={handleRemoveStarred}
            onDone={() => triggerExitAd(() => navigate("/"))}
          />
        )}
      </AnimatePresence>

      {/* Force-Pick Result Modal (timer expired) */}
      <AnimatePresence>
        {showResult && (
          <>
            <ConfettiEffect />
            <DiscoverResultModal
              pickedMovie={pickedMovie}
              onClose={() => setShowResult(false)}
              onDone={() => triggerExitAd(() => navigate("/"))}
            />
          </>
        )}
      </AnimatePresence>

      {/* Exit ad interstitial */}
      {exitAdVisible && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/90 backdrop-blur-md">
          <div className="w-full max-w-sm mx-4 rounded-2xl bg-gradient-card border border-border overflow-hidden shadow-card">
            <div className="aspect-[2/3] flex flex-col items-center justify-center bg-secondary relative">
              <div className="w-full px-4 flex-1 flex items-center justify-center flex-col gap-4">
                <span className="text-4xl">🍿</span>
                <p className="text-sm font-display font-semibold text-foreground text-center">Ayeris Premium</p>
                <p className="text-xs text-muted-foreground text-center max-w-[200px]">{t("goPremium.removeAds")}</p>
              </div>
              
              <div className="absolute bottom-4 right-4 z-30 flex flex-col items-end gap-2">
                {exitAdSkippable ? (
                  <button
                    onClick={dismissExitAd}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold active:scale-95 transition-transform"
                  >
                    {t("discover.adSkip")} →
                  </button>
                ) : (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-muted-foreground text-sm font-medium">
                    <div className="relative w-5 h-5">
                      <svg className="w-full h-full -rotate-90" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="8" fill="none" stroke="hsl(var(--border))" strokeWidth="2" />
                        <circle
                          cx="10" cy="10" r="8" fill="none"
                          stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round"
                          strokeDasharray={50.3}
                          strokeDashoffset={50.3 * (1 - exitAdTimer / 10)}
                          style={{ transition: 'stroke-dashoffset 1s linear' }}
                        />
                      </svg>
                    </div>
                    <span className="tabular-nums">{exitAdTimer}s</span>
                  </div>
                )}
                <button
                  onClick={() => setShowPremiumModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-accent-foreground text-xs font-semibold active:scale-95 transition-transform"
                >
                  ✨ {t("goPremium.removeAds")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <GoPremiumModal open={showPremiumModal} onClose={() => setShowPremiumModal(false)} trigger="ad_remove" />
    </div>
  );
};

export default Discover;
